"use client";

/**
 * User icon in the status bar (right of the toolbox) for TAI employees with
 * CSM access grants.
 *
 * Visibility rules (evaluated server-side via /api/employee/has-access):
 *  1. Signed-in user has a @tranquility.ai-family email (customer pool, or
 *     employee pool alone on /employee/app).
 *  2. An active employee-pool session with at least one CSM grant (strict).
 *  3. Not rendered on employee management pages (/employee/tenants, /employee/auth/*).
 *     Rendered on /employee/app (the full TimePilot interface in employee context).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { UserIcon } from "@phosphor-icons/react";
import { getGatewaySession } from "@/lib/gateway-fetch";
import { isTaiEmail } from "@/lib/tai-domains";
import {
  statusBarIconButtonProps,
  statusBarIconLinkClassName,
} from "./status-bar-icon-button";
import { rememberAppShellPath } from "@/lib/app-nav-state";
// Button stylesheet is loaded via other status-bar consumers; data-* attrs
// reuse the same `.tp-button` hover tokens for this link trigger.

/** Paths where the switcher should be suppressed (employee management UI, not the app). */
const EMPLOYEE_MANAGEMENT_PATHS = ["/employee/tenants", "/employee/auth"];

function isEmployeeManagementPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return EMPLOYEE_MANAGEMENT_PATHS.some((p) => pathname.startsWith(p));
}

/** Throttle repeat checks while the same customer session still has access. */
const POSITIVE_CACHE_MS = 60 * 1000;
const NEGATIVE_CACHE_MS = 15 * 1000;

function emailFromAccessToken(accessToken: string): string | undefined {
  const parts = accessToken.split(".");
  const b64 = parts[1]?.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 ? b64 + "=".repeat((4 - (b64.length % 4)) % 4) : "";
  const payload = padded ? (JSON.parse(atob(padded)) as Record<string, unknown>) : {};
  return payload["email"] as string | undefined;
}

type AccessSnapshot = {
  hasAccess: boolean;
  checkedAt: number;
  customerEmail: string | null;
};

let _accessSnapshot: AccessSnapshot | null = null;

function isSnapshotFresh(snapshot: AccessSnapshot): boolean {
  const ttl = snapshot.hasAccess ? POSITIVE_CACHE_MS : NEGATIVE_CACHE_MS;
  return Date.now() - snapshot.checkedAt < ttl;
}

function invalidateAccessSnapshot() {
  _accessSnapshot = null;
}

export default function EmployeeSwitcher() {
  const [show, setShow] = useState(false);
  const pathname = usePathname();
  const prevPathnameRef = useRef<string | undefined>(undefined);
  const checkGenerationRef = useRef(0);

  const runAccessCheck = useCallback(async (generation: number, useCache: boolean) => {
    try {
      const session = await getGatewaySession();
      const customerEmail = session ? emailFromAccessToken(session.accessToken) : null;

      if (
        useCache &&
        _accessSnapshot &&
        isSnapshotFresh(_accessSnapshot) &&
        _accessSnapshot.customerEmail === customerEmail
      ) {
        if (generation === checkGenerationRef.current) {
          setShow(_accessSnapshot.hasAccess);
        }
        return;
      }

      if (session && customerEmail && !isTaiEmail(customerEmail)) {
        _accessSnapshot = { hasAccess: false, checkedAt: Date.now(), customerEmail };
        if (generation === checkGenerationRef.current) setShow(false);
        return;
      }

      if (generation !== checkGenerationRef.current) return;

      const res = await fetch("/api/employee/has-access", {
        credentials: "include",
        cache: "no-store",
      });
      if (generation !== checkGenerationRef.current) return;

      const hasAccess = res.ok ? ((await res.json()) as { hasAccess: boolean }).hasAccess : false;
      _accessSnapshot = { hasAccess, checkedAt: Date.now(), customerEmail };
      setShow(hasAccess);
    } catch {
      if (generation === checkGenerationRef.current) {
        _accessSnapshot = { hasAccess: false, checkedAt: Date.now(), customerEmail: null };
        setShow(false);
      }
    }
  }, []);

  useEffect(() => {
    if (isEmployeeManagementPath(pathname)) {
      ++checkGenerationRef.current; // invalidate any in-flight check
      invalidateAccessSnapshot(); // clear module-level cache so next mount on /employee/app gets a fresh check
      setShow(false);
      prevPathnameRef.current = pathname;
      return;
    }

    const prev = prevPathnameRef.current;
    prevPathnameRef.current = pathname;

    if (isEmployeeManagementPath(prev)) {
      invalidateAccessSnapshot();
    }

    const generation = ++checkGenerationRef.current;
    void runAccessCheck(generation, true);
  }, [pathname, runAccessCheck]);

  useEffect(() => {
    // Re-check on tab return or window focus, reusing the cached snapshot
    // when it is still fresh. The 60s positive / 15s negative TTLs absorb
    // alt-tab churn — invalidating on every focus turned each alt-tab into
    // a server round trip for `/api/employee/has-access`, counting against
    // the per-IP WAF rate limit on the frontend ALB.
    //
    // We listen on both `focus` and `visibilitychange` because they cover
    // different cases: visibilitychange fires when the tab is hidden then
    // re-shown, but a window-level focus change (e.g. switching between
    // two browser windows that both show this tab) keeps visibility state
    // at "visible" and only triggers focus. Double-fires on real tab
    // returns just hit the fresh cache and early-return — no extra server
    // calls.
    function check() {
      if (isEmployeeManagementPath(pathname)) return;
      const generation = ++checkGenerationRef.current;
      void runAccessCheck(generation, true);
    }

    function onVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      check();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", check);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", check);
    };
  }, [pathname, runAccessCheck]);

  if (!show) return null;

  return (
    <a
      href="/employee/tenants"
      aria-label="Switch tenant"
      title="Switch tenant"
      data-style={statusBarIconButtonProps.style}
      data-color={statusBarIconButtonProps.color}
      data-background={statusBarIconButtonProps.background}
      data-size={statusBarIconButtonProps.size}
      data-icon-only="true"
      className={statusBarIconLinkClassName}
      onClick={() => rememberAppShellPath(window.location.pathname)}
    >
      {/* AGENT: do not restyle this trigger. statusBarIconLinkClassName only;
          regular (not duotone) glyph. */}
      <span className="tp-button-icon" aria-hidden="true">
        <UserIcon size={22} weight="regular" />
      </span>
    </a>
  );
}
